import { useEffect } from "react";
import { Link } from "react-router-dom";

const SECTIONS = [
  {
    label: "Universe",
    links: [
      { to: "/", label: "Home" },
      { to: "/collections", label: "Collections" },
      { to: "/journal", label: "Journal" },
    ],
  },
  {
    label: "Studio",
    links: [
      { to: "/vision", label: "Vision" },
      { to: "/about", label: "About" },
      { to: "/faq", label: "FAQ" },
      { to: "/contact", label: "Contact" },
    ],
  },
  {
    label: "Shop",
    links: [
      { to: "/product/africa-frame", label: "The Africa Frame™" },
      { to: "/#watch", label: "The Timepiece · Coming Soon" },
    ],
  },
];

export default function MenuOverlay({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="menu-overlay"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-2xl text-white"
      role="dialog"
      aria-modal="true"
    >
      {/* Ambient chrome gradient */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 80% 20%, rgba(212,175,55,0.10) 0%, transparent 55%), radial-gradient(circle at 20% 80%, rgba(192,192,192,0.06) 0%, transparent 60%)",
        }}
      />
      <div className="grain-overlay" />

      <div className="relative z-[1] h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col">
        {/* Top bar */}
        <div className="h-[72px] flex items-center justify-between">
          <Link
            to="/"
            onClick={onClose}
            className="font-display text-[1.6rem] tracking-[0.32em] text-white"
          >
            AFRQA<span className="gold text-[0.65rem] align-super ml-0.5">™</span>
          </Link>
          <button
            type="button"
            onClick={onClose}
            data-testid="menu-overlay-close"
            className="font-label text-[#cfcfca] hover:text-white flex items-center gap-3"
            aria-label="Close menu"
          >
            <span className="w-6 h-px bg-current" />
            <span>Close</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center py-10 overflow-y-auto">
          <nav className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-12">
            {SECTIONS.map((sec) => (
              <div key={sec.label}>
                <span className="font-label text-[#8a8a85]">{sec.label}</span>
                <ul className="mt-6 space-y-4">
                  {sec.links.map((l) => (
                    <li key={l.to + l.label}>
                      <Link
                        to={l.to}
                        onClick={onClose}
                        data-testid={`menu-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="font-display text-[clamp(1.6rem,2.6vw,2.4rem)] leading-tight text-white hover:text-[#d4af37] transition-colors link-underline"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          {/* Phase II teaser */}
          <aside className="lg:col-span-4 border-l-0 lg:border-l border-[#1f1f1f] lg:pl-10">
            <span className="font-label text-[#d4af37]">— Phase II —</span>
            <p className="mt-4 font-editorial italic text-[clamp(1.6rem,2.4vw,2.4rem)] leading-snug text-white">
              The future speaks{" "}
              <span className="gold-text-gradient not-italic font-display">
                your language.
              </span>
            </p>
            <p className="mt-5 text-[#a3a3a3] font-light max-w-sm">
              Adaptive Vision. Future Systems. A new wearable language —
              loading.
            </p>
            <Link
              to="/vision"
              onClick={onClose}
              data-testid="menu-phase-2-cta"
              className="btn-ghost-gold mt-8 inline-flex"
            >
              Read the Manifesto
            </Link>
          </aside>
        </div>

        {/* Bottom: contact strip */}
        <div className="py-8 border-t border-[#1a1a1a] grid grid-cols-1 md:grid-cols-3 gap-6 text-[#cfcfca]">
          <div>
            <span className="font-label text-[#8a8a85]">Email</span>
            <p className="mt-2">
              <a
                href="mailto:Afrqa@afrqa.com"
                className="font-display text-white link-underline"
              >
                Afrqa@afrqa.com
              </a>
            </p>
          </div>
          <div>
            <span className="font-label text-[#8a8a85]">Studio</span>
            <p className="mt-2 font-display text-white">
              Los Angeles, California
            </p>
          </div>
          <div>
            <span className="font-label text-[#8a8a85]">Follow</span>
            <p className="mt-2">
              <a
                href="https://instagram.com/shopafrqa"
                target="_blank"
                rel="noreferrer"
                className="font-display text-white link-underline"
              >
                @shopafrqa
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
